// Convex hooks for data operations

import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';

// ============ Schools Hooks ============

export function useSchools() {
  return useQuery(api.schools.getAll);
}

export function useActiveSchools() {
  return useQuery(api.schools.getActive);
}

export function useSchool(schoolId: Id<'schools'> | undefined) {
  return useQuery(api.schools.getById, schoolId ? { schoolId } : 'skip');
}

export function useSchoolMutations() {
  const create = useMutation(api.schools.create);
  const update = useMutation(api.schools.update);
  const toggleActive = useMutation(api.schools.toggleActive);
  const regenerateAccessKey = useMutation(api.schools.regenerateAccessKey);
  const remove = useMutation(api.schools.remove);

  return { create, update, toggleActive, regenerateAccessKey, remove };
}

// ============ Registrations Hooks ============

export function useRegistrations() {
  return useQuery(api.registrations.getAll);
}

export function useRegistration(registrationId: Id<'registrations'> | undefined) {
  return useQuery(api.registrations.getById, registrationId ? { registrationId } : 'skip');
}

export function useSchoolRegistration(schoolId: Id<'schools'> | undefined) {
  return useQuery(api.registrations.getBySchoolId, schoolId ? { schoolId } : 'skip');
}

export function useRegistrationsByStatus(status: 'draft' | 'submitted' | 'approved' | 'rejected') {
  return useQuery(api.registrations.getByStatus, { status });
}

export function useRegistrationStatistics() {
  return useQuery(api.registrations.getStatistics);
}

export function useRegistrationValidation(registrationId: Id<'registrations'> | undefined) {
  return useQuery(api.registrations.validate, registrationId ? { registrationId } : 'skip');
}

export function useRegistrationMutations() {
  const createDraft = useMutation(api.registrations.createDraft);
  const update = useMutation(api.registrations.update);
  const submit = useMutation(api.registrations.submit);
  const approve = useMutation(api.registrations.approve);
  const reject = useMutation(api.registrations.reject);
  const edit = useMutation(api.registrations.edit);
  const updateStudent = useMutation(api.registrations.updateStudent);
  const remove = useMutation(api.registrations.remove);

  return { createDraft, update, submit, approve, reject, edit, updateStudent, remove };
}

// ============ Events Hooks ============

export function useEvents() {
  return useQuery(api.events.getAll);
}

export function useActiveEvents() {
  return useQuery(api.events.getActive);
}

export function useEvent(eventId: Id<'events'> | undefined) {
  return useQuery(api.events.getById, eventId ? { eventId } : 'skip');
}

export function useEventsByCategory(category: 'tech' | 'science' | 'logic' | 'creative') {
  return useQuery(api.events.getByCategory, { category });
}

export function useEventMutations() {
  const create = useMutation(api.events.create);
  const update = useMutation(api.events.update);
  const toggleStatus = useMutation(api.events.toggleStatus);
  const incrementRegistrations = useMutation(api.events.incrementRegistrations);
  const remove = useMutation(api.events.remove);

  return { create, update, toggleStatus, incrementRegistrations, remove };
}

// ============ Results Hooks ============

export function useResults() {
  return useQuery(api.results.getEventResults);
}

export function useResultsByEvent(eventId: Id<'events'> | undefined) {
  return useQuery(api.results.getByEvent, eventId ? { eventId } : 'skip');
}

export function useResultsBySchool(schoolId: Id<'schools'> | undefined) {
  return useQuery(api.results.getBySchool, schoolId ? { schoolId } : 'skip');
}

export function useLeaderboard() {
  return useQuery(api.results.getLeaderboard);
}

export function useEventResults() {
  return useQuery(api.results.getEventResults);
}

export function useResultMutations() {
  const create = useMutation(api.results.create);
  const update = useMutation(api.results.update);
  const remove = useMutation(api.results.remove);

  return { create, update, remove };
}

// ============ Committee Hooks ============

export function useCommitteeMembers() {
  return useQuery(api.committee.getAll);
}

export function useActiveCommitteeMembers() {
  return useQuery(api.committee.getActive);
}

export function useCommitteeMember(memberId: Id<'committee_members'> | undefined) {
  return useQuery(api.committee.getById, memberId ? { memberId } : 'skip');
}

export function useCommitteeMutations() {
  const create = useMutation(api.committee.create);
  const update = useMutation(api.committee.update);
  const toggleActive = useMutation(api.committee.toggleActive);
  const remove = useMutation(api.committee.remove);
  const reorder = useMutation(api.committee.reorder);

  return { create, update, toggleActive, remove, reorder };
}

// ============ Admin Hooks ============

export function useAdmins() {
  return useQuery(api.adminAuth.getAllAdmins);
}

export function useAdminMutations() {
  const add = useMutation(api.adminAuth.addAdmin);
  const remove = useMutation(api.adminAuth.removeAdmin);
  const deleteAdmin = useMutation(api.adminAuth.deleteAdmin);

  return { add, remove, deleteAdmin };
}

// ============ Logging Hooks ============

export function useLogs(limit?: number) {
  return useQuery(api.logging.getAll, limit ? { limit } : {});
}

export function useRecentLogs(limit: number = 50) {
  return useQuery(api.logging.getRecent, { limit });
}

export function useLogMutations() {
  const logAdminLogin = useMutation(api.logging.logAdminLogin);
  const logSchoolLogin = useMutation(api.logging.logSchoolLogin);
  const logAdminActivity = useMutation(api.logging.logAdminActivity);
  const logSchoolActivity = useMutation(api.logging.logSchoolActivity);
  const clearAll = useMutation(api.logging.clearAll);

  return { logAdminLogin, logSchoolLogin, logAdminActivity, logSchoolActivity, clearAll };
}

// ============ Storage Hooks ============

export function useStorageMutations() {
  const generateUploadUrl = useMutation(api.storage.generateUploadUrl);
  const deleteFile = useMutation(api.storage.deleteFile);
  const storeStudentPhoto = useMutation(api.storage.storeStudentPhoto);
  const storeEventIcon = useMutation(api.storage.storeEventIcon);
  const storeEventCover = useMutation(api.storage.storeEventCover);
  const storeCommitteePhoto = useMutation(api.storage.storeCommitteePhoto);

  return {
    generateUploadUrl,
    deleteFile,
    storeStudentPhoto,
    storeEventIcon,
    storeEventCover,
    storeCommitteePhoto,
  };
}

// ============ File Upload Helper ============

export function useFileUpload() {
  const { generateUploadUrl } = useStorageMutations();

  const uploadFile = async (file: File): Promise<Id<'_storage'>> => {
    // Get upload URL from Convex
    const uploadUrl = await generateUploadUrl();

    // Upload the file
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    const { storageId } = await response.json();
    return storageId;
  };

  return { uploadFile };
}
